import { SuppressKeyboardEventParams } from '@ag-grid-community/core';
import { capacityStore, setDefaultStore } from '@store/capacityTools/store';
import { PreferredLaneT } from '@store/carriers/types';
import { UserAutoComplete } from '@typeDefinitions/userTypes';
import { CapacityFinderFormT } from '@views/capacityFinder/form';

import {
  EquipmentSelect,
  LocationSelect,
  ObjectSetter,
  TruckT,
  defaultsT,
  editTruckT,
} from './types';

export const getEquipmentValueSelect = (equipment: string) => {
  const eq = capacityStore.equipment.find((e) => {
    return e.category === equipment;
  });

  if (eq !== undefined) {
    return eq.name;
  }

  return '';
};

export const handleDefaultValueSelect = function <
  T extends TruckT | editTruckT | PreferredLaneT | CapacityFinderFormT,
>(data: () => T, isOrigin: boolean) {
  if (isOrigin) {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (Boolean(data().originState && data().originCity)) {
      return `${data().originCity}, ${data().originState}`;
    }

    return '';
  }

  if (Boolean(data().destinationState) && Boolean(data().destinationCity)) {
    return `${data().destinationCity}, ${data().destinationState}`;
  }

  return '';
};

export const swapOriginAndDestination = (
  data: CapacityFinderFormT,
  setData: ObjectSetter<CapacityFinderFormT>,
) => {
  const origin = data.origin;
  const originCity = data.originCity;
  const originState = data.originState;
  const originCountry = data.originCountry;
  const originPostal = data.originPostal;
  const originLatitude = data.originLatitude;
  const originLongitude = data.originLongitude;
  const originRadiusMiles = data.originRadiusMiles;

  setData('origin', data.destination);
  setData('originCity', data.destinationCity);
  setData('originState', data.destinationState);
  setData('originCountry', data.destinationCountry);
  setData('originPostal', data.destinationPostal);
  setData('originLatitude', data.destinationLatitude);
  setData('originLongitude', data.destinationLongitude);
  setData('originRadiusMiles', data.destinationRadiusMiles);

  setData('destination', origin);
  setData('destinationCity', originCity);
  setData('destinationState', originState);
  setData('destinationCountry', originCountry);
  setData('destinationPostal', originPostal);
  setData('destinationLatitude', originLatitude);
  setData('destinationLongitude', originLongitude);
  setData('destinationRadiusMiles', originRadiusMiles);
};

export const handleLocationSelect = (
  setData: ObjectSetter<TruckT | editTruckT>,
  item: LocationSelect,
  isOrigin: boolean,
) => {
  if (isOrigin) {
    setData('originCity', item.city);
    setData('originState', item.state);
    setData('originCountry', item.country);
    setData('originPostal', item.zipcode);
    setData('originLatitude', item.lat);
    setData('originLongitude', item.long);
  } else {
    setData('destinationCity', item.city);
    setData('destinationState', item.state);
    setData('destinationCountry', item.country);
    setData('destinationPostal', item.zipcode);
    setData('destinationLatitude', item.lat);
    setData('destinationLongitude', item.long);
  }
};

export const handleLocationClear = (
  setData: ObjectSetter<TruckT | editTruckT>,
  isOrigin: boolean,
) => {
  if (isOrigin) {
    setData('originCity', null);
    setData('originState', null);
    setData('originPostal', null);
    setData('originLatitude', null);
    setData('originLongitude', null);
  } else {
    setData('destinationCity', null);
    setData('destinationState', null);
    setData('destinationPostal', null);
    setData('destinationLatitude', null);
    setData('destinationLongitude', null);
  }
};

export const handleEquipmentSelect = (
  setData: ObjectSetter<
    | (TruckT & { equipmentVal: string })
    | (editTruckT & { equipmentVal: string })
  >,
  item: EquipmentSelect,
) => {
  setData('equipment', item.value);
  setData(
    'equipmentVal',
    capacityStore.equipment.find((e) => {
      return e.category === item.value;
    })?.name ?? '',
  );
};

export const handleLocationDefaultClear = (
  setData: ObjectSetter<defaultsT>,
  isOrigin: boolean,
) => {
  if (isOrigin) {
    setData('defaultOriginCity', null);
    setData('defaultOriginState', null);
    setData('defaultOriginPostal', null);
    setData('defaultOriginLatitude', null);
    setData('defaultOriginLongitude', null);
  } else {
    setData('defaultDestinationCity', null);
    setData('defaultDestinationState', null);
    setData('defaultDestinationPostal', null);
    setData('defaultDestinationLatitude', null);
    setData('defaultDestinationLongitude', null);
  }
};

export const handleDefaultLocationSelect = (
  setData: ObjectSetter<defaultsT>,
  item: LocationSelect,
  isOrigin: boolean,
) => {
  if (isOrigin) {
    setData('defaultOriginCity', item.city);
    setData('defaultOriginState', item.state);
    setData('defaultOriginCountry', item.country);
    setData('defaultOriginPostal', item.zipcode);
    setData('defaultOriginLatitude', item.lat);
    setData('defaultOriginLongitude', item.long);
  } else {
    setData('defaultDestinationCity', item.city);
    setData('defaultDestinationState', item.state);
    setData('defaultDestinationCountry', item.country);
    setData('defaultDestinationPostal', item.zipcode);
    setData('defaultDestinationLatitude', item.lat);
    setData('defaultDestinationLongitude', item.long);
  }
};

export const resetDefaults = () => {
  setDefaultStore('carrierId', 0);
  setDefaultStore('defaultOriginCountry', 'US');
  setDefaultStore('defaultDestinationCountry', 'US');
  setDefaultStore('defaultOriginCity', '');
  setDefaultStore('defaultOriginState', '');
  setDefaultStore('defaultOriginPostal', '');
  setDefaultStore('defaultOriginRadiusMiles', 0);
  setDefaultStore('defaultDestinationCity', '');
  setDefaultStore('defaultDestinationState', '');
  setDefaultStore('defaultDestinationPostal', '');
  setDefaultStore('defaultDestinationRadiusMiles', 0);
  setDefaultStore('defaultEquipment', '');
  setDefaultStore('defaultOriginLatitude', 0);
  setDefaultStore('defaultOriginLongitude', 0);
  setDefaultStore('defaultDestinationLatitude', 0);
  setDefaultStore('defaultDestinationLongitude', 0);
};

export const defaultPrimaryRep = (): UserAutoComplete => {
  return {
    name: '',
    id: 0,
    office: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phone1: '',
    phone1Ext: 0,
    phone2: '',
    phone2Ext: 0,
    fax: '',
    officeId: 0,
    percentage: 0,
    active: false,
    postingGroupId: 0,
    accountingGroup: '',
    thumbnail: '',
  };
};

export const nameBidStyling = (status: string | undefined) => {
  if (status === 'Pending') {
    return 'bg-gray-500	text-white rounded-xs	text-sm self-center px-2';
  }
  if (status === 'Approved') {
    return 'bg-green-500	text-white rounded-xs	text-sm self-center px-2';
  }
  if (status === 'Rejected') {
    return 'bg-red-500	text-white rounded-xs	text-sm self-center px-2';
  }
  if (status === 'Booked') {
    return 'bg-yellow-500	text-white rounded-xs	text-sm self-center px-2';
  }
  if (status === 'Assigned') {
    return 'bg-yellow-100	text-white rounded-xs	text-sm self-center px-2';
  }
  return '';
};

export const statusStringFormatter = (status: string | undefined) => {
  if (status === 'Rejected') {
    return 'Denied';
  }
  return status;
};

export const suppressKeyboardEvent = (params: SuppressKeyboardEventParams) => {
  const e = params.event;

  if (
    e.code == 'ArrowLeft' ||
    e.key == 'ArrowLeft' ||
    e.code == 'ArrowRight' ||
    e.key == 'ArrowRight'
  ) {
    return true;
  }

  return false;
};
